"use client";

import { CheckCircle, Clock, Loader2, X } from "lucide-react";
import {
  LeaveWaitlistModal,
  WaitlistModal,
} from "@punchup/ui-common/components/waitlist";
import React, { CSSProperties, useState } from "react";

import { ShowWithComedian } from "@punchup/data/legacy";
import { WaitlistSubscription } from "@punchup/data/client";
import { cn } from "@punchup/utils/cn";
import { useWaitlistStatus } from "../../hooks/useWaitlistStatus";

interface WaitlistButtonProps {
  show: ShowWithComedian;
  ownerId: string;
  isVenue: boolean;
  className?: string;
  style?: CSSProperties;
  comedianSlug?: string;
  venueSlug?: string;
  // When provided, skip the per-show fetch and use this subscription.
  // Pass `null` for "known-not-subscribed". Parents that render many rows
  // should batch via [[useMultipleWaitlistStatus]] and pass each row's
  // subscription down — see useWaitlistStatus.injectedSubscription.
  injectedSubscription?: WaitlistSubscription | null;
}

export const WaitlistButton: React.FC<WaitlistButtonProps> = ({
  show,
  ownerId,
  isVenue,
  className = "btn text-[14px] font-semibold leading-[26px] flex flex-row items-center justify-center py-2 px-2 w-full bg-primary hover:bg-primary-dark",
  style,
  comedianSlug,
  venueSlug,
  injectedSubscription,
}) => {
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);

  const iconClassNameValue = "w-4 h-4 mr-2";

  const {
    isSubscribed,
    subscription,
    isLoading,
    isJoining,
    joinError,
    leaveWaitlist,
    isLeaving,
    leaveError,
  } = useWaitlistStatus({
    showId: show.id,
    ownerId,
    isVenue,
    injectedSubscription,
  });

  if (isLoading) {
    return (
      <button
        disabled
        className={cn("opacity-75 cursor-not-allowed", className)}
        style={style}
      >
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        Checking...
      </button>
    );
  }

  if (isSubscribed && subscription) {
    return (
      <>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsLeaveModalOpen(true);
          }}
          disabled={isLeaving}
          className={cn("transition-colors", className)}
          style={style}
        >
          {isLeaving ? (
            <>
              <Loader2 className={cn("animate-spin", iconClassNameValue)} />
              Leaving...
            </>
          ) : (
            <>
              <X className={iconClassNameValue} />
              Leave Waitlist
            </>
          )}
        </button>
        {leaveError && (
          <p className="text-xs text-red-600 text-center mt-1">
            Failed to leave waitlist. Please try again.
          </p>
        )}

        {isLeaveModalOpen && (
          <LeaveWaitlistModal
            show={show}
            isLeaving={isLeaving}
            onClose={() => setIsLeaveModalOpen(false)}
            onConfirm={async () => {
              leaveWaitlist(
                { subscriptionId: subscription.id },
                {
                  onSuccess: () => {
                    setIsLeaveModalOpen(false);
                  },
                }
              );
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsWaitlistModalOpen(true);
        }}
        disabled={isJoining}
        className={className}
        style={style}
      >
        {isJoining ? (
          <>
            <Loader2 className={cn("animate-spin", iconClassNameValue)} />
            Joining...
          </>
        ) : (
          <>
            <Clock className={iconClassNameValue} />
            Join Waitlist
          </>
        )}
      </button>

      {joinError && (
        <p className="text-xs text-red-600 text-center">
          Failed to join waitlist. Please try again.
        </p>
      )}

      {isWaitlistModalOpen && (
        <WaitlistModal
          show={show}
          isVenue={isVenue}
          ownerId={ownerId}
          onClose={() => {
            setIsWaitlistModalOpen(false);
          }}
          comedianSlug={comedianSlug}
          venueSlug={venueSlug}
        />
      )}
    </>
  );
};

export const WaitlistButtonLight: React.FC<{
  isSubscribed: boolean;
  onJoin: () => void;
  isLoading?: boolean;
  className?: string;
  style?: CSSProperties;
}> = ({
  isSubscribed,
  onJoin,
  isLoading = false,
  className = "btn-primary w-full",
  style,
}) => {
  if (isLoading) {
    return (
      <button
        disabled
        className={cn("opacity-75 cursor-not-allowed", className)}
        style={style}
      >
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        Checking...
      </button>
    );
  }

  if (isSubscribed) {
    return (
      <button
        disabled
        className={cn(
          "flex items-center justify-center gap-2 cursor-not-allowed",
          className
        )}
        style={style}
      >
        <CheckCircle className="w-4 h-4" />
        On Waitlist
      </button>
    );
  }

  return (
    <button
      onClick={onJoin}
      className={cn("flex items-center justify-center gap-2", className)}
      style={style}
    >
      <Clock className="w-4 h-4" />
      Join Waitlist
    </button>
  );
};
